<template>
  <GenericCard
    has-delete
    has-documents
    :document-types="documentTypes"
    :document-identifier="documentIdentifier"
    :locked="locked"
    :subtitle="subtitle"
    :on-delete="onCardDelete"
    :on-action="submitForm"
    :on-refresh="onCardRefresh"
    :invalid="v$.section.$invalid"
    :dirty="v$.section.$anyDirty"
    :create="create"
    title="Immigration Status"
    :section="section"
    has-footer
    has-share
    :card-identifier="cardIdentifier"
    :survey-validation="surveyValidation"
    :survey-identifier="surveyIdentifier"
  >
    <template #header>
      <v-btn
        small
        @click="showHistoryDialog = true"
      >
        Restore as Current
      </v-btn>
    </template>
    <v-form
      :disabled="locked"
      @submit.prevent="submitForm"
    >
      <!-- Immigration Status and it's requirements -->
      <v-row>
        <!-- autocomplete immigration_status:immigrationStatusOptions -->
        <v-col cols="4">
          <v-autocomplete
            v-model="section.immigration_status"
            :items="immigrationStatusOptions"
            label="Immigration Status*"
            outlined
            :error-messages="getErrorMessages(v$.section.immigration_status)"
            @input="v$.section.immigration_status.$touch()"
            @blur="v$.section.immigration_status.$touch()"
          />
        </v-col>
        <!-- autocomplete admission_category:admissionCategoryOptions -->
        <v-col
          v-if="fieldRules.admission_category"
          cols="4"
        >
          <v-autocomplete
            v-model="section.admission_category"
            :items="admissionCategoryOptions"
            label="Admission Category*"
            outlined
            :error-messages="getErrorMessages(v$.section.admission_category)"
            @input="v$.section.admission_category.$touch()"
            @blur="v$.section.admission_category.$touch()"
          />
        </v-col>
        <!-- text certificate_number -->
        <v-col
          v-if="fieldRules.certificate_number"
          cols="4"
        >
          <v-text-field
            v-model="section.certificate_number"
            autocomplete="off"
            label="Certificate Number*"
            outlined
            :error-messages="getErrorMessages(v$.section.certificate_number)"
            @input="v$.section.certificate_number.$touch()"
            @blur="v$.section.certificate_number.$touch()"
          />
        </v-col>
        <!-- text naturalization_certificate_number -->
        <v-col
          v-if="fieldRules.naturalization_certificate_number"
          cols="4"
        >
          <v-text-field
            v-model="section.naturalization_certificate_number"
            autocomplete="off"
            label="Naturalization Certificate Number*"
            outlined
            :error-messages="getErrorMessages(v$.section.naturalization_certificate_number)"
            @input="v$.section.naturalization_certificate_number.$touch()"
            @blur="v$.section.naturalization_certificate_number.$touch()"
          />
        </v-col>
        <!-- text immigration_status_other -->
        <v-col
          v-if="fieldRules.immigration_status_other"
          cols="4"
        >
          <v-text-field
            v-model="section.immigration_status_other"
            autocomplete="off"
            label="Immigration Status Other*"
            outlined
            :error-messages="getErrorMessages(v$.section.immigration_status_other)"
            @input="v$.section.immigration_status_other.$touch()"
            @blur="v$.section.immigration_status_other.$touch()"
          />
        </v-col>
        <!-- autocomplete niv_type:nivTypeOptions -->
        <v-col
          v-if="fieldRules.niv_type"
          cols="4"
        >
          <v-autocomplete
            v-model="section.niv_type"
            :items="nivTypeOptions"
            label="Niv Type*"
            outlined
            :error-messages="getErrorMessages(v$.section.niv_type)"
            @input="v$.section.niv_type.$touch()"
            @blur="v$.section.niv_type.$touch()"
          />
        </v-col>
      </v-row>

      <!-- Data field -->
      <v-row>
        <v-col
          cols="4"
          class="pt-0"
        >
          <v-text-field
            v-model="section.i_94"
            autocomplete="off"
            label="I-94 Number"
            maxlength="11"
            outlined
            :error-messages="getErrorMessages(v$.section.i_94)"
            @input="v$.section.i_94.$touch()"
            @blur="v$.section.i_94.$touch()"
          />
        </v-col>
      </v-row>

      <!-- Dates -->
      <v-row
        v-if="!fieldRules.notes_only"
      >
        <!-- combo-date date_obtained -->
        <v-col
          cols="8"
        >
          <FormatSelectableComboDate
            class="mt-3 ml-1"
            label="Date Obtained"
            :formatable-date="section.date_obtained"
            :format="section.date_obtained_format"
            :uncertain="section.date_obtained_uncertain"
            required
            outlined
            :disabled="locked"
            for-party-edit
            :error-messages="getErrorMessages(v$.section.date_obtained)"
            @update-box="onDateObtainedUncertainUpdate"
            @update-date="onDateObtainedUpdate"
            @tab-out="v$.section.date_obtained.$touch()"
          />
        </v-col>
        <!-- text place_obtained -->
        <v-col
          cols="4"
          class="pt-0"
        >
          <v-text-field
            v-model="section.place_obtained"
            autocomplete="off"
            label="Place Obtained"
            outlined
            :error-messages="getErrorMessages(v$.section.place_obtained)"
            @input="v$.section.place_obtained.$touch()"
            @blur="v$.section.place_obtained.$touch()"
          />
        </v-col>
        <!-- text place_obtained_other -->
        <!-- combo-date date_expires -->
        <v-col
          v-if="!fieldRules.no_date_expires"
          cols="8"
        >
          <FormatSelectableComboDate
            class="mt-3 ml-1"
            label="Date Expires"
            :formatable-date="section.date_expires"
            :format="section.date_expires_format"
            :uncertain="section.date_expires_uncertain"
            required
            outlined
            :disabled="locked"
            for-party-edit
            :error-messages="getErrorMessages(v$.section.date_expires)"
            @update-box="onDateExpiresUncertainUpdate"
            @update-date="onDateExpiresUpdate"
            @tab-out="v$.section.date_expires.$touch()"
          />
        </v-col>
      </v-row>

      <v-row>
        <!-- text-area notes -->
        <v-col>
          <v-textarea
            v-model="section.notes"
            label="Notes"
            rows="4"
            outlined
            :error-messages="getErrorMessages(v$.section.notes)"
            @input="v$.section.notes.$touch()"
            @blur="v$.section.notes.$touch()"
          />
        </v-col>
      </v-row>
    </v-form>
    <ImmigrationStatusHistoryDialog
      v-model="showHistoryDialog"
      :history="true"
      :subtitle-immigration-status="subtitleImmigrationStatus"
      @confirm="onRestoreToCurrent"
    />
  </GenericCard>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'

import { helpers, required, requiredIf } from '@vuelidate/validators'
import { useVuelidate } from '@vuelidate/core'

import { PARTY_NAMESPACE } from '@/store/party'
import { SHARED_NAMESPACE } from '@/store/shared'

import { alertHandler } from '@/alertHandler'
import { immigrationStatusReqChecker } from '@/store/party/partials/party-edit/helpers/pe-immigration-status-card.js'

import FormatSelectableComboDate from '@/components/Fields/FormatSelectableComboDate'
import GenericCard from '@/components/Party/PartyEdit/SectionCards/GenericCard'
import ImmigrationStatusHistoryDialog from '@/components/Dialogs/Party/ImmigrationStatusHistoryDialog'

export default {
  name: 'ImmigrationStatusHistoriesCard',
  components: { FormatSelectableComboDate, GenericCard, ImmigrationStatusHistoryDialog },

  props: {
    item: {
      type: Object,
      required: true,
    },

    surveyIdentifier: {
      type: String,
      default: null,
    },

    surveyValidation: {
      type: Boolean,
      default: false,
    },
  },

  setup () {
    return {
      v$: useVuelidate(),
      ...alertHandler(),
    }
  },

  data () {
    return {
      documentTypes: [
        { text: 'Immigration Status Document', value: 'immigration-status-document' },
        { text: 'Other', value: 'other' },
      ],

      section: {},
      showHistoryDialog: false,
    }
  },

  validations () {
    return {
      section: {
        admission_category: {
          required: helpers.withMessage(
            'Admission Category is required',
            requiredIf(this.fieldRules.admission_category)
          ),
        },

        certificate_number: {
          required: helpers.withMessage(
            'Certificate Number is required',
            requiredIf(this.fieldRules.certificate_number)
          ),
        },

        date_expires: { },
        date_expires_uncertain: {},
        date_obtained: { },
        date_obtained_uncertain: {},
        i_94: {
          validFormat: helpers.withMessage(
            'I-94 must be exactly 11 characters (letters and digits only)',
            value => {
              if (!value) return true // not required
              return /^[A-Za-z0-9]{11}$/.test(value)
            }
          ),
        },

        immigration_status: { required: helpers.withMessage('Immigration Status is required', required) },
        immigration_status_other: {
          required: helpers.withMessage(
            'Immigration Status Other is required',
            requiredIf(this.fieldRules.immigration_status_other)
          ),
        },

        naturalization_certificate_number: {
          required: helpers.withMessage(
            'Naturalization Certificate Number is required',
            requiredIf(this.fieldRules.naturalization_certificate_number)
          ),
        },

        niv_type: { required: helpers.withMessage('NIV Type is required', requiredIf(this.fieldRules.niv_type)) },
        notes: {},
        place_obtained: {},
      },
    }
  },

  computed: {
    admissionCategoryOptions () { return this.getCachedDropdown('immigration_statuses/admission_category') },
    cardIdentifier () { return `immigration-status-history-card-${this.sectionIdentifier}` },
    create () { return this.item.id === null },
    documentIdentifier () { return `details-immigration-status-history-${this.sectionIdentifier}` },
    fieldRules () { return immigrationStatusReqChecker(this.section.immigration_status) },
    immigrationStatusOptions () { return this.getCachedDropdown('immigration_statuses/immigration_status') },

    locked: {
      get () {
        return this.peImmigrationStatusHistoriesCardLock(this.sectionIdentifier)
      },

      set (value) {
        this.setPeImmigrationStatusHistoriesCardLock({ index: this.sectionIdentifier, locked: value })
      },
    },

    nivTypeOptions () { return this.getCachedDropdown('immigration_statuses/niv_type') },
    sectionIdentifier () { return this.section.id || 'new' },

    subtitle () {
      if (this.create) return ''
      if (this.fieldRules.immigration_status_other) return '(Other)'
      if (this.fieldRules.admission_category) return `(${this.subtitleImmigrationStatus} - ${this.admissionCategoryOptions?.find(a => a.value === this.section.admission_category)?.text})`
      if (this.fieldRules.certificate_number) return `(${this.subtitleImmigrationStatus} - ${this.section.certificate_number})`
      if (this.fieldRules.naturalization_certificate_number) return `(${this.subtitleImmigrationStatus} - ${this.section.naturalization_certificate_number})`
      if (this.fieldRules.niv_type) return `(${this.subtitleImmigrationStatus} - ${this.nivTypeOptions?.find(n => n.value === this.section.niv_type)?.text})`
      return `(${this.subtitleImmigrationStatus})`
    },

    subtitleImmigrationStatus () { return this.immigrationStatusOptions?.find(a => a.value === this.section.immigration_status)?.text },

    ...mapGetters(PARTY_NAMESPACE, [
      'peImmigrationStatusHistoriesCardLock',
    ]),

    ...mapGetters(SHARED_NAMESPACE, [
      'getCachedDropdown',
    ]),
  },

  watch: {
    item: {
      immediate: true,

      handler: function () {
        this.section = { ...this.item }
      },

      deep: true,
    },
  },

  created () {
    if (!this.create) this.initPeImmigrationStatusHistoriesCard(this.sectionIdentifier)

    this.getDropdownOptions({ path: 'immigration_statuses/immigration_status' })
    this.getDropdownOptions({ path: 'immigration_statuses/niv_type' })
    this.getDropdownOptions({ path: 'immigration_statuses/admission_category' })
  },

  methods: {
    getErrorMessages (validator) { return validator?.$errors.map(e => e.$message) },

    async onCardCreate () {
      return this.peImmigrationStatusHistoriesCardCreate({ index: this.sectionIdentifier, section: this.section })
    },

    async onCardDelete () {
      if (this.create) return this.peImmigrationStatusHistoriesCardCreateUndo()
      else {
        return this.peImmigrationStatusHistoriesCardDelete({ id: this.section.id, index: this.sectionIdentifier })
      }
    },

    async onCardRefresh () {
      return this.peImmigrationStatusHistoriesCardRefresh({ index: this.sectionIdentifier, section: this.section })
    },

    async onCardUpdate () {
      return this.peImmigrationStatusHistoriesCardUpdate({
        id: this.section.id,
        index: this.sectionIdentifier,
        section: this.section,
      })
    },

    onDateExpiresUncertainUpdate (value) {
      this.v$.section.date_expires.$touch()
      this.v$.section.date_expires_uncertain.$touch()
      this.section.date_expires_uncertain = value
    },

    onDateExpiresUpdate (value) {
      this.v$.section.date_expires.$touch()
      this.v$.section.date_expires_uncertain.$touch()
      this.section.date_expires = value.date
      this.section.date_expires_format = value.format
    },

    onDateObtainedUncertainUpdate (value) {
      this.v$.section.date_obtained.$touch()
      this.v$.section.date_obtained_uncertain.$touch()
      this.section.date_obtained_uncertain = value
    },

    onDateObtainedUpdate (value) {
      this.v$.section.date_obtained.$touch()
      this.v$.section.date_obtained_uncertain.$touch()
      this.section.date_obtained = value.date
      this.section.date_obtained_format = value.format
    },

    async onRestoreToCurrent () {
      this.section.current = true
      this.showHistoryDialog = false
      await this.onCardUpdate()
      this.pePatchCharacteristicDelete({ characteristic: 'immigration_status_histories', id: this.section.id })
    },

    async onSectionUnlock () {
      return this.setPeImmigrationStatusHistoriesCardLock({ index: this.sectionIdentifier, locked: false })
    },

    async submitForm () {
      if (this.locked) return this.onSectionUnlock()
      else if (this.create) return this.onCardCreate()
      else return this.onCardUpdate()
    },

    ...mapMutations(PARTY_NAMESPACE, [
      'peImmigrationStatusHistoriesCardCreateUndo',
      'pePatchCharacteristicDelete',
      'setPeImmigrationStatusHistoriesCardLock',

    ]),

    ...mapActions(PARTY_NAMESPACE, [
      'initPeImmigrationStatusHistoriesCard',
      'peImmigrationStatusHistoriesCardCreate',
      'peImmigrationStatusHistoriesCardDelete',
      'peImmigrationStatusHistoriesCardRefresh',
      'peImmigrationStatusHistoriesCardUpdate',
    ]),

    ...mapActions(SHARED_NAMESPACE, [
      'getDropdownOptions',
    ]),
  },
}
</script>
